// İSG uzmanı portalı — Personel çekmecesinin belge listesi (GL5; §15, §14 GL #13). Hem kayıt
// (taşeron Personel listesi) hem izin (izin detayı Personel sekmesi) bağlamında AYNI bileşen:
// belgeler dışarıdan gelir (kayıtta `GET .../documents`, izinde `personnel.documents`), bu bileşen
// yalnız gösterir ve eylemleri yürütür — indir, onayla, reddet (satır-içi gerekçe), sil, ekle.
// Bağlama özgü alt küme YOK.
//
// Onay/red/silme/ekleme sonrası `invalidatePersonnelDocumentCaches` tek çağrıyla kayıt belge
// sorgusunu, her izin detayını, izin listelerini ve kontrol aşamasını tazeler — iki bağlam da buna
// dayanır. Silinen satırın tetikleyicisi kalkınca odak "Belge Ekle"ye iner (VehicleDocuments deseni).
//
// Tür etiketi §27 kaydından (`label('personnel', type)`; kayıt yoksa ham kod). Dosya satırında yapay
// zekâ tanıma işareti (`RecognitionMark`, GL #7): uzman onay/red verirken sınıflandırmanın kaynağını
// görür — "AI v{n}" ya da "AI tanıması yok". Geçerlilik/onay durum satırı değişmez.
//
// **KVKK/PII.** İndirme §8 presigned GET ile kısa ömürlü; UI PII/dosya URL'i log'lamaz.

import {
  Button,
  ConfirmDialog,
  DocumentRow,
  EmptyState,
  type PersonnelDocumentDto,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  contractorsApi,
  currentCulture,
  documentNameDetail,
  documentValidityText,
  presentDocument,
  useDocumentTypes,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { invalidatePersonnelDocumentCaches } from '../../lib/personnelCache';
import {
  DocumentRejectRow,
  DocumentReviewActions,
  DocumentReviewFeedback,
  type ReviewSource,
  documentNameLabel,
  useDocumentReview,
} from '../documents/DocumentReviewActions';
import { RecognitionMark } from '../documents/RecognitionMark';
import { PersonnelDocumentLinkModal } from './PersonnelDocumentLinkModal';

export interface PersonnelDocumentsProps {
  contractorUuid: string;
  /** `SupplierUser.Uuid` (kişi) — bağ satırı uuid'i DEĞİL. */
  userUuid: string;
  /** `null` → okunamadı (eski API'de izin satırında `documents` dizi değil): "belge yok" DEĞİL. */
  documents: readonly PersonnelDocumentDto[] | null;
  /** Belgeleri getiren sorgunun damgaları (kayıt belge sorgusu ya da izin detayı). */
  source: ReviewSource;
}

export function PersonnelDocuments({
  contractorUuid,
  userUuid,
  documents,
  source,
}: PersonnelDocumentsProps) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState<PersonnelDocumentDto | null>(null);
  const [deletedUuid, setDeletedUuid] = useState<string | null>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const rows = documents ?? [];

  const review = useDocumentReview({
    approve: (linkUuid) =>
      contractorsApi.approvePersonnelDocument(contractorUuid, userUuid, linkUuid),
    reject: (linkUuid, reason) =>
      contractorsApi.rejectPersonnelDocument(contractorUuid, userUuid, linkUuid, { reason }),
    // Onay kişinin bağlı olduğu HER izni ve kontrol aşamasını etkiler.
    invalidate: () => invalidatePersonnelDocumentCaches(queryClient, contractorUuid, userUuid),
    documents: rows,
    source,
    fallbackFocusRef: uploadButtonRef,
    overlayOpen: addOpen || toDelete !== null,
  });

  // DELETE hedefi §15.1'de **link kaydının kendi `uuid`'idir**, §8 `documentUuid` DEĞİL.
  const deleteMutation = useMutation({
    mutationFn: (linkUuid: string) => {
      review.clearFeedback();
      return contractorsApi.deletePersonnelDocument(contractorUuid, userUuid, linkUuid);
    },
    onSuccess: (_data, linkUuid) => {
      // Silme hazırlığı değiştirebilir (§17.2) → izin detayları ve kontrol aşaması da tazelenir.
      invalidatePersonnelDocumentCaches(queryClient, contractorUuid, userUuid);
      setDeletedUuid(linkUuid);
      setToDelete(null);
    },
  });

  // Silinen satır listeden KALKINCA ve diyalog kapanınca odak <body>'ye düşmesin.
  useEffect(() => {
    if (
      deletedUuid !== null &&
      toDelete === null &&
      !rows.some((row) => row.uuid === deletedUuid)
    ) {
      uploadButtonRef.current?.focus();
      setDeletedUuid(null);
    }
  }, [rows, deletedUuid, toDelete]);

  const title = t('permits.personnelDocs.title');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        <Button
          ref={uploadButtonRef}
          variant="secondary"
          size="sm"
          onClick={() => setAddOpen(true)}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {t('contractors.personnelDocs.add.new')}
        </Button>
      </div>

      <DocumentReviewFeedback review={review} />
      <p className="text-xs text-muted">{t('contractors.personnelDocs.kvkk')}</p>

      <Table caption={title}>
        <TableHeader>
          <TableRow className="carbon:h-0">
            <TableHead className="h-0 p-0 carbon:h-0">
              <span className="sr-only">{t('permits.personnelDocs.col.type')}</span>
            </TableHead>
            <TableHead className="h-0 p-0 text-right carbon:h-0">
              <span className="sr-only">{t('permits.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents === null ? (
            <TableMessageRow colSpan={2}>{t('error.generic')}</TableMessageRow>
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={2}>
              <EmptyState icon={FileText} title={t('contractors.personnelDocs.empty')} />
            </TableMessageRow>
          ) : (
            rows.map((document) => {
              const typeLabel = label('personnel', document.type);
              const p = presentDocument(document, t);
              const validity = documentValidityText(document, (iso) => formatDate(iso, culture), t);
              const nameDetail = documentNameDetail(document, validity);
              const deleteName = t('documents.review.delete', {
                type: documentNameLabel(typeLabel, nameDetail),
              });
              return (
                <DocumentRow
                  key={document.uuid}
                  title={typeLabel}
                  fileName={document.fileName}
                  provenance={<RecognitionMark release={document.recognitionRelease} />}
                  noteText={document.note}
                  validityRangeText={validity}
                  quiet={p.quiet}
                  quietLabel={p.quietLabel}
                  tags={p.tags}
                  srExtra={p.srExtra}
                  statusContextLabel={t('permits.personnelDocs.col.validityStatus')}
                  rejectionReasonText={
                    document.approvalStatus === 'Rejected' && document.rejectionReason
                      ? t('documents.review.rejectionReason', { reason: document.rejectionReason })
                      : null
                  }
                  actions={
                    <DocumentReviewActions
                      review={review}
                      document={{
                        uuid: document.uuid,
                        documentUuid: document.documentUuid,
                        approvalStatus: document.approvalStatus,
                        typeLabel,
                        nameDetail,
                      }}
                      deleteSlot={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setToDelete(document)}
                          aria-label={deleteName}
                          title={deleteName}
                        >
                          <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                        </Button>
                      }
                    />
                  }
                  editorRow={
                    <DocumentRejectRow review={review} documentUuid={document.uuid} colSpan={2} />
                  }
                />
              );
            })
          )}
        </TableBody>
      </Table>

      <PersonnelDocumentLinkModal
        open={addOpen}
        onOpenChange={setAddOpen}
        contractorUuid={contractorUuid}
        userUuid={userUuid}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setToDelete(null);
            deleteMutation.reset();
            // Satır hâlâ listedeyse bayat odak hedefi bırakma.
            if (toDelete && rows.some((row) => row.uuid === toDelete.uuid)) {
              setDeletedUuid(null);
            }
          }
        }}
        title={t('contractors.personnelDocs.delete.title')}
        body={t('contractors.personnelDocs.delete.body', {
          type: toDelete ? label('personnel', toDelete.type) : '',
        })}
        confirmLabel={t('contractors.personnelDocs.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
